import { FC, useEffect, useRef, useState } from "react";
import Layout from "@/components/layout";
import styles from "./index.module.scss";
import Receipt from "../../components/receipt";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/button";
import isE2e from "@/helpers/isE2e";
import DigitalReceipt from "./digital-receipt";
import Review from "./review";
import Close from "@/assets/images/Close";
import CheckmarkCircle from "@/assets/images/CheckmarkCircle";
import useNavigateToStart from "@/hooks/useNavigateToStart";
import { useIsTakeOut } from "@/stores/cash-register";
import { isPickupReceipt } from "@/helpers/receipts";
import EmailConfirmation from "./email-confirmation";
import { useAppSelector } from "@/hooks/useAppSelector";
import { selectReceipt } from "@/store/receipt/selectors";

const inactiveCountSeconds = isE2e ? 3 : 15;
const initialCount = isE2e ? 5 : 45;

const SuccessPage: FC = () => {
  const { t } = useTranslation();
  const receipt = useAppSelector(selectReceipt);
  const isTakeOut = useIsTakeOut();
  const navigateToStart = useNavigateToStart();
  const [count, setCount] = useState<number | undefined>();
  const countRef = useRef<number | undefined>(count);
  countRef.current = count;
  const latestEventTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      if (countRef.current !== undefined && countRef.current < 1) return navigateToStart();
      if (Date.now() - inactiveCountSeconds * 1000 > latestEventTimeRef.current) {
        setCount((prev) => (prev ?? initialCount) - 1);
      }
    }, 1000);
    const onKeydown = () => {
      setCount(undefined);
      latestEventTimeRef.current = Date.now();
    };
    document.addEventListener("keydown", onKeydown);
    return () => clearInterval(interval);
  }, [navigateToStart]);

  if (!receipt) return null;

  return (
    <Layout childrenClassName={styles.layoutContentBox} hideTopBar>
      <div className={styles.closeButtonRow}>
        <Close onClick={navigateToStart} className={styles.closeButtonMargin} />
      </div>
      {isTakeOut ? (
        <h1 className={styles.title} data-cy="success-page-title">
          <CheckmarkCircle />
          {t("successPage.paid")}
        </h1>
      ) : (
        <h1 className={styles.pickupTitle} data-cy="success-page-pickup-title">
          {t("pickup.successPage.thanks")}
        </h1>
      )}
      {isPickupReceipt(receipt) && <EmailConfirmation email={receipt.customer.emailAddress} />}
      <div className={styles.contentContainer}>
        <div className={styles.row}>
          <Receipt
            products={receipt?.lineItems.products}
            discounts={receipt.lineItems.discounts}
            productTotalAmountCents={receipt.productTotalAmountCents}
            amountCents={receipt.amountCents}
            pickupStartAt={isPickupReceipt(receipt) ? receipt.pickupStartAt : undefined}
            depositTotalAmountCents={receipt.depositTotalAmountCents}
            vatCents={receipt.vatCents}
            className={styles.column}
            successPage
          />
          <div className={styles.column}>
            {isTakeOut && <DigitalReceipt className={styles.row} />}
            <Review className={styles.row} />
          </div>
        </div>
        <Button
          text={`${t("ui.close")} ${count ? ` (${count})` : ""}`}
          size="large"
          onClick={navigateToStart}
          data-cy="close-button"
        />
      </div>
    </Layout>
  );
};

export default SuccessPage;
